import { useState } from 'react';

import {
  Button,
  Badge,
  Card,
  CardContent,
  EmptyState,
  PageHeader,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogFooter,
} from '@/components/ui';
import { ProjetosIcon } from '@/components/icons';
import { useDepartments, useDepartmentActions } from '@/lib/admin-store';
import type { Department } from '@/lib/admin-types';

import { DepartmentFormDialog } from './DepartmentFormDialog';

/**
 * Tela admin: Departamentos.
 *
 * Lista de departamentos em grid de cards (mais visual que tabela).
 * Cada card mostra: nome, descrição, contagem de usuários, status (ativo/inativo).
 * Ações: editar, excluir.
 */
export function DepartmentsPage() {
  const departments = useDepartments();
  const { deleteDepartment, pending } = useDepartmentActions();

  const [editing, setEditing] = useState<Department | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Department | null>(null);

  async function handleConfirmDelete() {
    if (!confirmDelete) return;
    await deleteDepartment(confirmDelete.id);
    setConfirmDelete(null);
  }

  // Ordena por campo `ordem` pra exibir conforme prioridade visual
  const sorted = [...departments].sort((a, b) => a.ordem - b.ordem);

  return (
    <section className="min-w-0">
      <PageHeader
        crumb="Configurações · Pessoas"
        title="Departamentos"
        subtitle={
          departments.length === 0
            ? 'Áreas da empresa que organizam pessoas e fluxos de trabalho.'
            : `${departments.length} ${departments.length === 1 ? 'departamento' : 'departamentos'} · ${departments.filter((d) => d.ativo).length} em uso.`
        }
        actions={<Button onClick={() => setCreating(true)}>Novo departamento</Button>}
      />

      {sorted.length === 0 ? (
        <Card variant="flat">
          <EmptyState
            icon={<ProjetosIcon className="w-6 h-6" />}
            title="Nenhum departamento criado"
            description="Crie o primeiro departamento pra organizar as pessoas e os fluxos de trabalho."
            action={<Button onClick={() => setCreating(true)}>Criar departamento</Button>}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sorted.map((d) => (
            <Card key={d.id} className="flex min-w-0 flex-col">
              <CardContent className="min-w-0 flex-1 p-6">
                <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                  <h2 className="min-w-0 font-display text-xl text-ink-1 dark:text-ink-dark-1 tracking-tight">
                    {d.nome}
                  </h2>
                  {d.ativo ? (
                    <Badge variant="success" dot size="sm" className="shrink-0">
                      Em uso
                    </Badge>
                  ) : (
                    <Badge variant="neutral" dot size="sm" className="shrink-0">
                      Desativado
                    </Badge>
                  )}
                </div>
                <p className="mb-4 min-h-[3em] text-sm leading-relaxed text-ink-2 dark:text-ink-dark-2">
                  {d.descricao || (
                    <span className="italic text-ink-3 dark:text-ink-dark-3">Sem descrição</span>
                  )}
                </p>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs">
                  <span className="min-w-0 break-all font-mono text-ink-3 dark:text-ink-dark-3">
                    /menu/{d.slug}
                  </span>
                  <span className="shrink-0 text-ink-2 dark:text-ink-dark-2">
                    {d.userCount ?? 0} {d.userCount === 1 ? 'pessoa' : 'pessoas'}
                  </span>
                </div>
              </CardContent>
              <div className="flex flex-wrap items-center gap-1 px-6 pb-4">
                <Button size="sm" variant="ghost" onClick={() => setEditing(d)}>
                  Editar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red hover:bg-red-soft dark:hover:bg-red-soft-dark"
                  onClick={() => setConfirmDelete(d)}
                  disabled={(d.userCount ?? 0) > 0}
                  title={
                    (d.userCount ?? 0) > 0
                      ? 'Tire as pessoas deste departamento antes de excluir'
                      : undefined
                  }
                >
                  Excluir
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal criar/editar */}
      {(creating || editing) && (
        <DepartmentFormDialog
          open
          department={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}

      {/* Confirmação de exclusão */}
      <Dialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        size="sm"
      >
        {confirmDelete && (
          <>
            <DialogHeader>
              <DialogTitle>Excluir departamento?</DialogTitle>
            </DialogHeader>
            <DialogContent>
              <p className="text-sm text-ink-2 dark:text-ink-dark-2">
                Tem certeza que quer excluir o departamento <strong>{confirmDelete.nome}</strong>?
                Os fluxos de trabalho e os cards ligados a ele ficam sem departamento.
              </p>
            </DialogContent>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                loading={pending === confirmDelete.id}
                onClick={handleConfirmDelete}
              >
                Excluir departamento
              </Button>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </section>
  );
}
